/**
 * These public pages need to be loaded in a traditional way
 * else we would get flash of the Dashboard on these routes
 */
import { defineAsyncComponent, nextTick } from 'vue'
import { useAuthStore } from '@/store/pinia/auth'
import { useDevStore } from '@/store/pinia/dev'
import { useLayoutStore } from '@/store/pinia/layout'
import { useOrganizationStore } from '@/store/pinia/organization'
import { useCloudCostManagementStore } from '@/store/pinia/organization/cloudCostManagement'
import { useLicenceStore } from '@/store/pinia/organization/licence'
import { useRootStore } from '@/store/pinia/root'
import { appMode } from '@/utils/config/mode'
import REGEX from '@/utils/config/regex'
import { getValidOrganization } from '@/utils/helpers'
import {
  gtmOrganizationsEvents,
  gtmObservabilityEvents,
  gtmProjectsEvents,
  gtmSecurityEvents,
  gtmStacksEvents,
} from '@/utils/helpers/analytics'
import { getPlacements } from '@/utils/placements'
import i18n from '@/utils/plugins/i18n'
import * as permissions from '@/utils/plugins/permissions'
import { actionsWithResources } from './routesPermissions'

/**
 * Plugin-widget routes are statically declared so Vue Router resolves params on
 * cold-load. The placements plugin populates the registry with widget metadata
 * via a navigation guard; this beforeEnter checks the registry and falls through
 * to the global notFound when the widget id does not exist for the given scope.
 *
 * @param {'org' | 'component'} scope
 */
const ensurePluginWidgetExists = (scope) => async (to) => {
  const placementId = `plugin-widget-${scope}-${to.params.widgetSlug}`
  const placement = getPlacements().find(({ id }) => id === placementId)
  if (!placement) return { name: 'notFound', state: { 0: to.fullPath }, replace: true }

  const slug = placement.widgetSlug || to.params.widgetSlug
  if (!permissions.checkUserPermissions('GetPluginWidgetData', slug)) {
    return { name: 'forbidden', replace: true }
  }
}

const alphaRoutes = []
const betaAppSections = [
  'dev',
]

const fitOnScreen = true
const handlerPage = true
const hasConditionalBreadcrumb = true
const hideFooter = true
const isListPage = true
const isPublic = true
const isTwoPane = true
const noPadding = true
const remainOnPageAfterOrgSwitch = true
const showBackBtn = true

/** Only works on project routes atm */
const noTabs = true

const isStagingDevOrTesting = appMode.isDev || appMode.isStaging || appMode.isTest

/** @typedef {import('vue-router').RouteRecord} VueRouteRecord */

/** @type {VueRouteRecord} */
const dashboard = {
  path: 'dashboard',
  name: 'dashboard',
  component: () => import('@/pages/CyPageDashboard.vue'),
  meta: {
    ...getMetaDefaults('dashboard', {
      permissionsKey: 'organization.dashboard',
    }),
    layout: {
      name: 'app',
    },
  },
  beforeEnter: (to, _from) => {
    const isLegacyURL = _.isEmpty(to.params.orgCanonical)
    if (!isLegacyURL) return
    const { orgCanonical } = useOrganizationStore()
    return { name: to.name, params: { orgCanonical } }
  },
}

/** @type {VueRouteRecord} */
const organizations = {
  path: '/organizations',
  name: 'organizations',
  component: () => import('@/pages/CyPageOrganizations.vue'),
  meta: {
    ...getMetaDefaults('organizations', {
      permissionsKey: 'organizations.index',
      isListPage,
      remainOnPageAfterOrgSwitch,
    }),
    layout: {
      name: 'app',
      fitOnScreen,
    },
  },
}

// ! DEPRECATED routes
// Sync removal of each redirect with publicly communicating the removal to customers
// We use these redirects instead of alias, so the route is converted to the correct one.

/**
 * Mapping of route paths to redirect route names.
 *
 * Each entry is a tuple:
 * [0] - The original route path (string) that should trigger a redirect
 * [1] - The target route name (string) to redirect to
 *
 * These mappings are used to generate Vue Router redirect routes dynamically.
 *
 * @type {[string, string][]}
 */
const redirectMappings = [
  ['/organizations/manage', 'organizations'],
  ['/organizations/projects/:orgCanonical/manage', 'projects'],
  ['/organizations/:orgCanonical/projects/from-stack', 'newProject'],
  ['/organizations/projects/:orgCanonical/:projectCanonical', 'project'],
  ['/organizations/:orgCanonical/projects/:projectCanonical/metrics', 'projectOverview'],
  ['/organizations/:orgCanonical/projects/:projectCanonical/summary', 'projectOverview'],
  ['/dashboard', 'dashboard'],
  ['/organizations/:orgCanonical/configuration/general', 'orgSettings'],
  ['/organizations/:orgCanonical/configuration/terraform', 'orgSettingsTerraform'],
  ['/organizations/:orgCanonical/configuration/workers', 'workers'],
  ['/organizations/:orgCanonical/stacks/from-template', 'stackFromTemplate'],
]

/** @type {VueRouteRecord[]} */
export const redirects = [
  ...redirectMappings.map(([path, name]) => ({
    path,
    redirect: (to) => ({ ...to, name }),
  })),
  {
    path: '/organizations/:orgCanonical/projects/:projectCanonical/pipelines/:pipelineCanonical',
    redirect: (to) => {
      const { params: { projectCanonical, pipelineCanonical } } = to
      const envCanonical = pipelineCanonical.replace(`${projectCanonical}-`, '')
      return { ...to, name: 'envComponentPipeline', params: { ...to.params, envCanonical } }
    },
  },
  {
    path: '/organizations/:orgCanonical/projects/:projectCanonical/pipelines/:pipelineCanonical/jobs/:jobCanonical/builds/:buildId',
    redirect: (to) => {
      const { params: { projectCanonical, pipelineCanonical } } = to
      const envCanonical = pipelineCanonical.replace(`${projectCanonical}-`, '')
      return { ...to, name: 'builds', params: { ...to.params, envCanonical } }
    },
  },
]

// Nested inside of org route
/** @type {VueRouteRecord[]} */
const catalogRepositories = [
  {
    path: 'catalog-repositories',
    name: 'catalogRepositories',
    component: () => import('@/pages/CyPageCatalogRepositories.vue'),
    props: true,
    meta: {
      ...getMetaDefaults('stacks', {
        permissionsKey: 'catalogRepositories.index',
        isListPage,
      }),
      layout: { name: 'app' },
      clickAction: ({ $gtm }) => {
        $gtm.trackEvent(gtmStacksEvents.stacksCatalogRepositories)
      },
    },
  },
  {
    path: 'catalog-repositories/create',
    name: 'newCatalogRepository',
    component: () => import('@/pages/CyPageCatalogRepositoryForm.vue'),
    props: true,
    meta: {
      ...getMetaDefaults('catalogRepositories', {
        permissionsKey: 'catalogRepositories.create',
        showBackBtn,
      }),
      layout: {
        name: 'app',
        fitOnScreen,
        hideFooter,
      },
    },
  },
  {
    path: 'catalog-repositories/:catalogRepositoryCanonical',
    name: 'catalogRepository',
    component: () => import('@/pages/CyPageCatalogRepository.vue'),
    props: true,
    meta: {
      ...getMetaDefaults('catalogRepositories', {
        permissionsKey: 'catalogRepositories.details',
        showBackBtn,
      }),
      layout: {
        name: 'app',
        fitOnScreen,
        hideFooter,
        noPadding,
        header: defineAsyncComponent(() => import('@/components/CyHeaderVariantCatalogRepository.vue')),
      },
    },
    beforeEnter: (to, _from) => {
      return redirectTo404IfHasInvalidParam(to)
    },
  },
  {
    path: 'catalog-repositories/:catalogRepositoryCanonical/edit',
    name: 'editCatalogRepository',
    component: () => import('@/pages/CyPageCatalogRepositoryForm.vue'),
    props: true,
    meta: {
      ...getMetaDefaults('catalogRepositories', {
        permissionsKey: 'catalogRepositories.details',
        showBackBtn,
      }),
      layout: {
        name: 'app',
        fitOnScreen,
        hideFooter,
      },
    },
    beforeEnter: (to, _from) => {
      return redirectTo404IfHasInvalidParam(to)
    },
  },
]
/** @type {VueRouteRecord[]} */
const configRepositories = [
  {
    path: 'config-repositories',
    name: 'configRepositories',
    component: () => import('@/pages/CyPageConfigRepositories.vue'),
    props: true,
    meta: {
      ...getMetaDefaults('configRepositories', {
        permissionsKey: 'configRepositories.index',
        isListPage,
      }),
      layout: { name: 'app' },
      clickAction: ({ $gtm }) => {
        $gtm.trackEvent(gtmProjectsEvents.configRepositories)
      },
    },
  },
  {
    path: 'config-repositories/create',
    name: 'newConfigRepository',
    component: () => import('@/pages/CyPageConfigRepository.vue'),
    props: true,
    meta: {
      ...getMetaDefaults('configRepositories', {
        permissionsKey: 'configRepositories.create',
        showBackBtn,
      }),
      layout: {
        name: 'app',
        fitOnScreen,
        hideFooter,
      },
    },
  },
  {
    path: 'config-repositories/:configRepositoryCanonical',
    name: 'configRepository',
    props: true,
    component: () => import('@/pages/CyPageConfigRepository.vue'),
    meta: {
      ...getMetaDefaults('configRepositories', {
        permissionsKey: 'configRepositories.details',
        showBackBtn,
      }),
      layout: {
        name: 'app',
        fitOnScreen,
        hideFooter,
      },
    },
    beforeEnter: (to, _from) => {
      return redirectTo404IfHasInvalidParam(to)
    },
  },
]
/** @type {VueRouteRecord[]} */
const credentials = [
  {
    path: 'credentials',
    name: 'credentials',
    component: () => import('@/pages/CyPageCredentials.vue'),
    props: true,
    meta: {
      ...getMetaDefaults('credentials', {
        permissionsKey: 'credentials.index',
        isListPage,
      }),
      layout: { name: 'app' },
      clickAction: ({ $gtm }) => {
        $gtm.trackEvent(gtmSecurityEvents.securityCredentials)
      },
    },
  },
  {
    path: 'credentials/create',
    name: 'newCredential',
    component: () => import('@/pages/CyPageCredential.vue'),
    props: true,
    meta: {
      ...getMetaDefaults('credentials', {
        permissionsKey: 'credentials.create',
        showBackBtn,
      }),
      layout: {
        name: 'app',
        fitOnScreen,
        hideFooter,
      },
    },
  },
  {
    path: 'credentials/:credentialCanonical',
    name: 'credential',
    component: () => import('@/pages/CyPageCredential.vue'),
    props: true,
    meta: {
      ...getMetaDefaults('credentials', {
        permissionsKey: 'credentials.details',
        showBackBtn,
      }),
      layout: {
        name: 'app',
        fitOnScreen,
        hideFooter,
      },
    },
    beforeEnter: (to, _from) => {
      return redirectTo404IfHasInvalidParam(to)
    },
  },
]
const members = [
  {
    path: 'members',
    name: 'members',
    component: () => import('@/pages/CyPageMembers.vue'),
    redirect: { name: 'orgSettings', query: { section: 'members' } },
    props: true,
    meta: {
      ...getMetaDefaults('members', {
        permissionsKey: 'members.index',
        isListPage,
      }),
      layout: {
        name: 'app',
        header: defineAsyncComponent(() => import('@/components/CyHeaderVariantOrganization.vue')),
      },
      clickAction: ({ $gtm }) => {
        $gtm.trackEvent(gtmOrganizationsEvents.organizationsMembers)
      },
    },
  },
  {
    path: 'members/invite',
    name: 'memberInvite',
    component: () => import('@/pages/CyPageMemberInvite.vue'),
    props: true,
    meta: {
      ...getMetaDefaults('members', {
        permissionsKey: 'user',
        backRouteTo: 'orgSettings?section=members',
        showBackBtn,
      }),
      requiredActions: ['GetOrgSettings'],
      layout: {
        name: 'app',
        fitOnScreen,
        hideFooter,
      },
    },
  },
  {
    path: 'members/:id',
    name: 'member',
    component: () => import('@/pages/CyPageMember.vue'),
    props: true,
    meta: {
      ...getMetaDefaults('members', {
        permissionsKey: 'members.details',
        backRouteTo: 'orgSettings?section=members',
        showBackBtn,
      }),
      layout: {
        name: 'app',
        fitOnScreen,
        hideFooter,
      },
    },
    beforeEnter: (to, _from) => {
      return redirectTo404IfHasInvalidParam(to)
    },
  },
]
/** @type {VueRouteRecord[]} */
const observability = [
  {
    path: 'observability',
    name: 'observabilitySection',
    component: () => import('@/pages/CyPageTabs.vue'),
    props: (route) => ({
      ...route.params,
      routeName: 'observabilitySection',
    }),
    meta: {
      ...getMetaDefaults('observabilitySection'),
      layout: { name: 'app' },
      tabs: () => ['events', 'pipelinesOverview'],
    },
    children: [
      {
        path: 'pipelines',
        name: 'pipelinesOverview',
        component: () => import('@/pages/CyPagePipelinesOverview.vue'),
        meta: {
          ...getMetaDefaults('observabilitySection', {
            permissionsKey: 'pipelines.index',
            isListPage,
          }),
          layout: { name: 'app' },
          clickAction: ({ $gtm }) => {
            $gtm.trackEvent(gtmObservabilityEvents.observabilityPipelinesOverview)
          },
        },
      },
      {
        path: 'events',
        name: 'events',
        component: () => import('@/pages/CyPageEvents.vue'),
        props: true,
        meta: {
          ...getMetaDefaults('observabilitySection', {
            permissionsKey: 'events.index',
            isListPage,
          }),
          layout: {
            name: 'app',
            fitOnScreen,
            hideFooter,
          },
          clickAction: ({ $gtm }) => {
            $gtm.trackEvent(gtmObservabilityEvents.observability)
          },
        },
      },
    ],
  },
]
/** @type {VueRouteRecord[]} */
const projects = [
  {
    path: 'projects',
    name: 'projectsSection',
    component: () => import('@/pages/CyPageTabs.vue'),
    props: (route) => {
      const tabsToHide = ['configRepository', 'newConfigRepository']

      return {
        ...route.params,
        routeName: 'projectsSection',
        hideTabs: tabsToHide.includes(route.name),
      }
    },
    meta: {
      ...getMetaDefaults('projects'),
      layout: { name: 'app' },
      tabs: () => ['projects', 'configRepositories'],
    },
    children: [
      {
        path: 'all-projects',
        name: 'projects',
        component: () => import('@/pages/CyPageProjects.vue'),
        props: true,
        meta: {
          ...getMetaDefaults('projects', {
            permissionsKey: 'projects.index',
            isListPage,
          }),
          layout: { name: 'app' },
          clickAction: ({ $gtm }) => {
            $gtm.trackEvent(gtmProjectsEvents.projects)
          },
        },
        beforeEnter: (to, _from) => {
          const isLegacyURL = to.path.startsWith('/projects')
          if (!isLegacyURL) return
          const [,, orgCanonical] = to.path.split('/')
          return { name: to.name, params: { orgCanonical } }
        },
      },
      ...configRepositories,
    ],
  },
  {
    path: 'projects/create',
    name: 'newProject',
    component: () => import('@/pages/CyPageProjectCreate.vue'),
    props: true,
    meta: {
      ...getMetaDefaults('projects', {
        permissionsKey: 'projects.index',
        backRouteTo: 'projects',
        showBackBtn,
      }),
      layout: {
        name: 'app',
        hideFooter,
      },
    },
  },
  {
    path: 'projects/:projectCanonical?',
    name: 'project',
    component: () => import('@/pages/CyPageProject.vue'),
    props: true,
    meta: {
      ...getMetaDefaults('projects', {
        permissionsKey: 'project.details',
        showBackBtn,
      }),
      layout: { name: 'app' },
    },
    beforeEnter: (to, _from) => {
      const isLegacyURL = to.path.startsWith('/projects')
      if (!isLegacyURL) return redirectTo404IfHasInvalidParam(to)
      const [,, orgCanonical, projectCanonical] = to.path.split('/')
      return { name: 'project', params: { orgCanonical, projectCanonical } }
    },
    children: [
      {
        path: 'empty',
        name: 'projectEmpty',
        component: () => import('@/pages/CyPageProjectEmpty.vue'),
        props: true,
        meta: {
          ...getMetaDefaults('projects', {
            permissionsKey: 'project.details',
            showBackBtn,
            backRouteTo: 'projects',
          }),
          layout: {
            name: 'app',
            header: defineAsyncComponent(() => import('@/components/CyHeaderVariantProject.vue')),
          },
          noTabs,
        },
      },
      {
        path: 'overview',
        name: 'projectOverview',
        component: () => import('@/pages/CyPageProjectOverview.vue'),
        props: true,
        meta: {
          ...getMetaDefaults('projects', {
            permissionsKey: 'project.environments.index',
          }),
          layout: {
            name: 'app',
            header: defineAsyncComponent(() => import('@/components/CyHeaderVariantProject.vue')),
            fitOnScreen,
            hideFooter,
          },
        },
        beforeEnter: (to, _from) => {
          return redirectTo404IfHasInvalidParam(to)
        },
      },
      {
        path: 'environments/:envCanonical',
        name: 'environment',
        component: () => import('@/pages/CyPageEnvironment.vue'),
        redirect: (to) => {
          return {
            ...to,
            name: 'environmentOverview',
          }
        },
        props: true,
        meta: {
          ...getMetaDefaults('environments'),
          layout: { name: 'app' },
        },
        children: [
          {
            path: 'overview',
            name: 'environmentOverview',
            component: () => import('@/pages/CyPageEnvironmentOverview.vue'),
            props: true,
            meta: {
              ...getMetaDefaults('projects', {
                permissionsKey: 'project.environments.index',
              }),
              layout: {
                name: 'app',
                header: defineAsyncComponent(() => import('@/components/CyHeaderVariantEnvironment.vue')),
                fitOnScreen,
                hideFooter,
              },
            },
            beforeEnter: (to, _from) => {
              return redirectTo404IfHasInvalidParam(to)
            },
          },
          {
            path: 'empty',
            name: 'environmentEmpty',
            component: () => import('@/pages/CyPageEnvironmentEmpty.vue'),
            props: true,
            meta: {
              ...getMetaDefaults('projects', {
                permissionsKey: 'project.environments.index',
              }),
              layout: {
                name: 'app',
                header: defineAsyncComponent(() => import('@/components/CyHeaderVariantEnvironment.vue')),
              },
              noTabs,
            },
          },
          {
            path: 'logs',
            name: 'logs',
            component: () => import('@/pages/CyPageLogs.vue'),
            props: true,
            meta: {
              ...getMetaDefaults('projects', {
                permissionsKey: 'project.logs.index',
              }),
              layout: {
                name: 'app',
                header: defineAsyncComponent(() => import('@/components/CyHeaderVariantEnvironment.vue')),
              },
            },
          },
          {
            path: 'activity',
            name: 'environmentActivity',
            component: () => import('@/pages/CyPageEnvironmentEvents.vue'),
            props: true,
            meta: {
              ...getMetaDefaults('projects', {
                permissionsKey: 'events.index',
                isListPage,
                hasConditionalBreadcrumb,
              }),
              layout: {
                name: 'app',
                header: defineAsyncComponent(() => import('@/components/CyHeaderVariantEnvironment.vue')),
                fitOnScreen,
                hideFooter,
              },
            },
          },
          {
            path: 'components/create',
            name: 'newEnvComponent',
            component: () => import('@/pages/CyPageEnvComponentCreate.vue'),
            props: true,
            meta: {
              ...getMetaDefaults('projects', {
                permissionsKey: 'project.create',
                backRouteTo: 'environment',
                showBackBtn,
              }),
              layout: { name: 'app', hideFooter },
              noTabs,
            },
          },
          {
            path: 'components/:componentCanonical',
            name: 'environmentComponent',
            component: () => import('@/pages/CyPageEnvComponent.vue'),
            redirect: (to) => {
              return {
                ...to,
                name: 'envComponentOverview',
              }
            },
            props: true,
            meta: {
              ...getMetaDefaults('projects'),
              layout: { name: 'app' },
            },
            children: [
              {
                path: 'overview',
                name: 'envComponentOverview',
                component: () => import('@/pages/CyPageEnvComponentOverview.vue'),
                props: true,
                meta: {
                  ...getMetaDefaults('projects', {
                    permissionsKey: 'components.details',
                  }),
                  layout: {
                    name: 'app',
                    header: defineAsyncComponent(() => import('@/components/CyHeaderVariantEnvComponent.vue')),
                    hideFooter,
                  },
                },
                beforeEnter: (to, _from) => {
                  return redirectTo404IfHasInvalidParam(to)
                },
              },
              {
                path: 'resources',
                name: 'envComponentInfrastructure',
                component: () => import('@/pages/CyPageEnvComponentInfrastructure.vue'),
                props: true,
                meta: {
                  ...getMetaDefaults('projects', {
                    permissionsKey: 'project.infraview.index',
                  }),
                  layout: {
                    name: 'app',
                    fitOnScreen,
                    header: defineAsyncComponent(() => import('@/components/CyHeaderVariantEnvComponent.vue')),
                    hideFooter,
                  },
                },
                beforeEnter: (to, _from) => {
                  const selectedTableMode = to.query.selected_table_mode
                  if (selectedTableMode) {
                    to.meta.selectedTableMode = selectedTableMode
                    to.query.selected_table_mode = null
                  }
                },
              },
              {
                path: 'metrics',
                name: 'envComponentMetrics',
                component: () => import('@/pages/CyPageEnvComponentMetrics.vue'),
                props: true,
                meta: {
                  ...getMetaDefaults('projects', {
                    permissionsKey: 'project.metrics',
                  }),
                  layout: {
                    name: 'app',
                    header: defineAsyncComponent(() => import('@/components/CyHeaderVariantEnvComponent.vue')),
                  },
                },
              },
              {
                path: 'activity',
                name: 'envComponentActivity',
                component: () => import('@/pages/CyPageEnvironmentEvents.vue'),
                props: true,
                meta: {
                  ...getMetaDefaults('projects', {
                    permissionsKey: 'events.index',
                    isListPage,
                  }),
                  layout: {
                    name: 'app',
                    header: defineAsyncComponent(() => import('@/components/CyHeaderVariantEnvComponent.vue')),
                    fitOnScreen,
                    hideFooter,
                  },
                },
              },
              {
                path: 'pipelines/:pipelineCanonical?',
                name: 'envComponentPipeline',
                component: () => import('@/pages/CyPageEnvComponentOverview.vue'),
                props: (route) => ({
                  ...route.params,
                  pipelineCanonical: route.params.pipelineCanonical
                    ?? `${route.params.projectCanonical}-${route.params.envCanonical}-${route.params.componentCanonical}`,
                }),
                meta: {
                  ...getMetaDefaults('projects', {
                    permissionsKey: 'project.pipelines.index',
                    backRouteTo: 'envComponentOverview',
                  }),
                  layout: {
                    name: 'app',
                    fitOnScreen,
                    header: defineAsyncComponent(() => import('@/components/CyHeaderVariantEnvComponent.vue')),
                    hideFooter,
                  },
                },
                beforeEnter: (to, _from) => {
                  if (to.params.pipelineCanonical?.startsWith('start-stop-')) {
                    to.meta.layout.header = null
                    to.meta.showBackBtn = true
                    to.meta.noTabs = true
                  } else {
                    to.meta.layout.header = defineAsyncComponent(() => import('@/components/CyHeaderVariantEnvComponent.vue'))
                    to.meta.showBackBtn = false
                    to.meta.noTabs = false
                  }
                },
              },
              {
                path: 'pipelines/:pipelineCanonical?/jobs/:jobCanonical',
                name: 'jobs',
                component: () => import('@/pages/CyPageBuilds.vue'),
                props: true,
                meta: {
                  ...getMetaDefaults('projects', {
                    permissionsKey: 'project.pipelines.builds',
                    backRouteTo: 'envComponentOverview',
                    showBackBtn,
                  }),
                  layout: { name: 'app' },
                  noTabs,
                },
              },
              {
                path: 'pipelines/:pipelineCanonical?/jobs/:jobCanonical/builds/:buildId?',
                name: 'builds',
                component: () => import('@/pages/CyPageBuilds.vue'),
                props: (route) => ({
                  ...route.params,
                  ...setIdProp(route),
                }),
                meta: {
                  ...getMetaDefaults('projects', {
                    permissionsKey: 'project.pipelines.builds',
                    backRouteTo: 'envComponentOverview',
                    showBackBtn,
                  }),
                  layout: { name: 'app' },
                  noTabs,
                },
              },
              {
                path: 'pipelines/:pipelineCanonical?/resources/:resourceCanonical',
                name: 'resource',
                component: () => import('@/pages/CyPageResource.vue'),
                props: true,
                meta: {
                  ...getMetaDefaults('projects', {
                    permissionsKey: 'project.pipelines.resources',
                    backRouteTo: 'envComponentOverview',
                    showBackBtn,
                  }),
                  layout: { name: 'app' },
                  noTabs,
                },
              },
              {
                path: 'configuration',
                name: 'envComponentConfiguration',
                component: () => import('@/pages/CyPageEnvComponentConfiguration.vue'),
                props: true,
                meta: {
                  ...getMetaDefaults('projects', {
                    permissionsKey: 'components.details',
                  }),
                  layout: {
                    name: 'app',
                    header: defineAsyncComponent(() => import('@/components/CyHeaderVariantEnvComponent.vue')),
                    fitOnScreen,
                    hideFooter,
                  },
                },
                beforeEnter: (to, _from) => {
                  return redirectTo404IfHasInvalidParam(to)
                },
              },
              {
                path: 'settings/:section(general|configuration|scheduling|terraform)?',
                name: 'envComponentSettings',
                component: () => import('@/pages/CyPageEnvComponentSettings.vue'),
                props: true,
                meta: {
                  ...getMetaDefaults('projects', {
                    permissionsKey: 'components.details',
                    backRouteTo: 'envComponentOverview',
                    showBackBtn,
                  }),
                  layout: {
                    name: 'app',
                    fitOnScreen,
                    hideFooter,
                  },
                  noTabs,
                },
              },
              {
                path: 'settings/plugin/:pluginId',
                name: 'envComponentSettingsPlugin',
                component: () => import('@/pages/CyPageEnvComponentSettings.vue'),
                props: true,
                meta: {
                  ...getMetaDefaults('projects', {
                    permissionsKey: 'components.details',
                    backRouteTo: 'envComponentOverview',
                    showBackBtn,
                  }),
                  layout: {
                    name: 'app',
                    fitOnScreen,
                    hideFooter,
                  },
                  noTabs,
                },
              },
              {
                path: 'infraview/create',
                name: 'newEnvComponentConfigInfraView',
                component: () => import('@/components/CyExternalBackendForm.vue'),
                props: true,
                meta: {
                  ...getMetaDefaults('projects', {
                    permissionsKey: 'project.infraview.create',
                    backRouteTo: 'envComponentSettings',
                    showBackBtn,
                  }),
                  layout: {
                    name: 'app',
                    variant: 'full-width',
                  },
                  noTabs,
                },
              },
              {
                path: 'infraview/:externalBackendId',
                name: 'envComponentConfigInfraView',
                component: () => import('@/components/CyExternalBackendForm.vue'),
                props: (route) => ({ ...route.params, ...setIdProp(route), useComponent: true }),
                meta: {
                  ...getMetaDefaults('projects', {
                    permissionsKey: 'project.infraview.details',
                    backRouteTo: 'envComponentSettings',
                    showBackBtn,
                  }),
                  layout: {
                    name: 'app',
                    variant: 'full-width',
                    hideFooter,
                  },
                  noTabs,
                },
                beforeEnter: (to, _from) => {
                  return redirectTo404IfHasInvalidParam(to)
                },
              },
              {
                path: 'widgets/:widgetSlug/:pathSuffix(.*)*',
                name: 'pluginWidgetComponent',
                component: () => import('@/pages/CyPageWidget.vue'),
                props: true,
                meta: {
                  ...getMetaDefaults('projects', { permissionsKey: 'components.details' }),
                  layout: {
                    name: 'app',
                    header: defineAsyncComponent(() => import('@/components/CyHeaderVariantEnvComponent.vue')),
                    hideFooter,
                  },
                  requiredActions: ['GetPluginWidgetData'],
                },
                beforeEnter: ensurePluginWidgetExists('component'),
              },
            ],
          },
        ],
      },
      {
        path: 'configuration',
        name: 'projectConfiguration',
        component: () => import('@/pages/CyPageProjectConfiguration.vue'),
        redirect: { name: 'projectConfigurationGeneral' },
        props: true,
        meta: {
          ...getMetaDefaults('projects', {
            permissionsKey: 'project.configuration',
            showBackBtn,
          }),
          layout: { name: 'app' },
          noTabs,
        },
        children: [
          {
            path: 'general',
            name: 'projectConfigurationGeneral',
            component: () => import('@/pages/CyPageProjectConfigurationGeneral.vue'),
            props: true,
            meta: {
              ...getMetaDefaults('projects', {
                permissionsKey: 'project.configuration',
                backRouteTo: 'project',
                showBackBtn,
              }),
              layout: {
                name: 'app',
                hideFooter,
              },
              noTabs,
            },
          },
          {
            path: 'logs',
            name: 'projectConfigurationLogs',
            component: () => import('@/pages/CyPageProjectConfigurationLogs.vue'),
            props: true,
            meta: {
              ...getMetaDefaults('projects', {
                permissionsKey: 'project.configuration',
                backRouteTo: 'project',
                showBackBtn,
              }),
              layout: {
                name: 'app',
                hideFooter,
              },
              noTabs,
            },
          },
          {
            path: 'logs/elasticsearch/create',
            name: 'newProjectConfigElasticSearch',
            component: () => import('@/pages/CyPageProjectConfigurationEsDetails.vue'),
            props: true,
            meta: {
              ...getMetaDefaults('projects', {
                permissionsKey: 'project.logs.elasticsearch.create',
                backRouteTo: 'projectConfigurationLogs',
                showBackBtn,
              }),
              layout: {
                name: 'app',
                variant: 'full-width',
              },
              noTabs,
            },
          },
          {
            path: 'logs/elasticsearch/:externalBackendId',
            name: 'projectConfigElasticSearch',
            component: () => import('@/pages/CyPageProjectConfigurationEsDetails.vue'),
            props: (route) => ({ ...route.params, ...setIdProp(route) }),
            meta: {
              ...getMetaDefaults('projects', {
                permissionsKey: 'project.logs.elasticsearch.details',
                backRouteTo: 'projectConfigurationLogs',
                showBackBtn,
              }),
              layout: {
                name: 'app',
                variant: 'full-width',
              },
              noTabs,
            },
            beforeEnter: (to, _from) => {
              return redirectTo404IfHasInvalidParam(to)
            },
          },
          {
            path: 'environments/:envCanonical',
            name: 'projectConfigurationEnvironment',
            component: () => import('@/pages/CyPageProjectConfigurationEnvironment.vue'),
            props: true,
            meta: {
              ...getMetaDefaults('projects', {
                permissionsKey: 'project.configuration',
                backRouteTo: 'projectOverview',
                showBackBtn,
              }),
              layout: {
                name: 'app',
                hideFooter,
              },
              noTabs,
            },
          },
        ],
      },
      {
        path: 'activity',
        name: 'projectActivity',
        component: () => import('@/pages/CyPageProjectEvents.vue'),
        props: true,
        meta: {
          ...getMetaDefaults('projects', {
            permissionsKey: 'events.index',
            isListPage,
            backRouteTo: 'projects',
          }),
          layout: {
            name: 'app',
            header: defineAsyncComponent(() => import('@/components/CyHeaderVariantProject.vue')),
            fitOnScreen,
            hideFooter,
          },
        },
      },
    ],
  },
]

/** @type {VueRouteRecord[]} */
const environment = [
  {
    path: 'environments/:envCanonical',
    name: 'orgEnvironmentSection',
    component: () => import('@/pages/CyPageTabs.vue'),
    props: (route) => ({
      ...route.params,
      routeName: 'orgEnvironmentSection',
    }),
    meta: {
      ...getMetaDefaults('orgEnvironmentSection', {
        permissionsKey: 'orgEnvironmentSection',
        backRouteTo: 'orgSettings?section=environment',
      }),
      layout: { name: 'app' },
      tabs: () => [
        'orgEnvironmentSectionOverview',
        'orgEnvironmentSectionVariables',
        'orgEnvironmentSectionSettings',
      ],
    },
    redirect: { name: 'orgEnvironmentSectionOverview' },
    children: [
      {
        path: 'overview',
        name: 'orgEnvironmentSectionOverview',
        component: () => import('@/pages/CyPageOrgEnvironmentOverview.vue'),
        props: true,
        meta: {
          ...getMetaDefaults('orgEnvironmentSection', {
            permissionsKey: 'orgEnvironmentSectionOverview',
            backRouteTo: 'orgSettings?section=environments',
            showBackBtn,
          }),
          layout: { name: 'app' },
        },
        beforeEnter: (to, _from) => {
          return redirectTo404IfHasInvalidParam(to)
        },
      },
      {
        path: 'variables',
        name: 'orgEnvironmentSectionVariables',
        component: () => import('@/pages/CyPageOrgEnvironmentVariables.vue'),
        props: true,
        meta: {
          ...getMetaDefaults('orgEnvironmentSection', {
            permissionsKey: 'orgEnvironmentSectionVariables',
            backRouteTo: 'orgSettings?section=environments',
            showBackBtn,
          }),
          layout: { name: 'app' },
        },
        beforeEnter: (to, _from) => {
          return redirectTo404IfHasInvalidParam(to)
        },
      },
      {
        path: 'settings',
        name: 'orgEnvironmentSectionSettings',
        component: () => import('@/pages/CyPageOrgEnvironmentSettings.vue'),
        props: true,
        meta: {
          ...getMetaDefaults('orgEnvironmentSection', {
            permissionsKey: 'orgEnvironmentSectionSettings',
            backRouteTo: 'orgSettings?section=environments',
            showBackBtn,
          }),
          layout: { name: 'app' },
        },
        beforeEnter: (to, _from) => {
          return redirectTo404IfHasInvalidParam(to)
        },
      },
    ],
  },
]

/** @type {VueRouteRecord[]} */
const cloudAccount = [
  {
    path: 'cloud-accounts/:cloudAccountCanonical',
    name: 'cloudAccountSection',
    component: () => import('@/pages/CyPageTabs.vue'),
    props: (route) => ({
      ...route.params,
      routeName: 'cloudAccountSection',
    }),
    meta: {
      ...getMetaDefaults('cloudAccountSection', {
        permissionsKey: 'cloudAccountSection',
        backRouteTo: 'orgSettings?section=cloudAccounts',
      }),
      layout: { name: 'app' },
      tabs: () => [
        'cloudAccountSectionEnvironments',
        'cloudAccountSectionSettings',
      ],
    },
    redirect: { name: 'cloudAccountSectionEnvironments' },
    children: [
      {
        path: 'environments',
        name: 'cloudAccountSectionEnvironments',
        component: () => import('@/pages/CyPageCloudAccountEnvironments.vue'),
        props: true,
        meta: {
          ...getMetaDefaults('cloudAccountSectionEnvironments', {
            permissionsKey: 'cloudAccountSectionEnvironments',
            backRouteTo: 'orgSettings?section=cloudAccounts',
            showBackBtn,
          }),
          layout: { name: 'app' },
        },
        beforeEnter: (to, _from) => {
          return redirectTo404IfHasInvalidParam(to)
        },
      },
      {
        path: 'settings',
        name: 'cloudAccountSectionSettings',
        component: () => import('@/pages/CyPageCloudAccountSettings.vue'),
        props: true,
        meta: {
          ...getMetaDefaults('cloudAccountSectionSettings', {
            permissionsKey: 'cloudAccountSectionSettings',
            backRouteTo: 'orgSettings?section=cloudAccounts',
            showBackBtn,
          }),
          layout: { name: 'app' },
        },
        beforeEnter: (to, _from) => {
          return redirectTo404IfHasInvalidParam(to)
        },
      },
    ],
  },
]

/** @type {VueRouteRecord[]} */
const cloudCostManagement = [
  {
    path: 'finops-greenops',
    name: 'cloudCostManagementSection',
    component: () => import('@/pages/CyPageTabs.vue'),
    props: (route) => {
      const ccmStore = useCloudCostManagementStore()
      const importingAccounts = ccmStore.getImportingAccounts
      const erroredAccounts = ccmStore.getErroredAccounts

      return {
        ...route.params,
        routeName: 'cloudCostManagementSection',
        hideTabs: ['cloudCostManagementWelcome', 'cloudCostManagementProviderDetail'].includes(route.name),
        tabsIcons: [
          {
            condition: !_.isEmpty(importingAccounts),
            name: 'autorenew',
            color: 'accent',
            targetTab: 'cloudCostManagementSettings',
            tooltip: i18n.global.t('cloudCostManagement.importingAccounts', importingAccounts.length, { named: { nb: importingAccounts.length } }),
          },
          {
            condition: !_.isEmpty(erroredAccounts),
            name: 'mdi-alert-outline',
            color: 'error',
            targetTab: 'cloudCostManagementSettings',
            tooltip: i18n.global.t('cloudCostManagement.erroredAccounts', erroredAccounts.length, { named: { nb: erroredAccounts.length } }),
          },
        ],
      }
    },
    meta: {
      ...getMetaDefaults('cloudCostManagementSection', {
        permissionsKey: 'cloudCostManagement.index',
      }),
      layout: { name: 'app' },
      tabs: () => [
        'cloudCostManagementDashboard',
        'cloudCostManagementSettings',
      ],
    },
    beforeEnter: async ({ name, params }, _from) => {
      const orgStore = useOrganizationStore()
      const shouldRedirect = name !== 'cloudCostManagementWelcome' && !(await hasCCMAccounts())
      if (shouldRedirect) return { name: 'cloudCostManagementWelcome', params }
      if (_.isEmpty(orgStore.available.cloudProviders)) {
        orgStore.FETCH_AVAILABLE({ keyPath: 'cloudProviders' })
      }
    },
    children: [
      {
        path: 'dashboard',
        name: 'cloudCostManagementDashboard',
        component: () => import('@/pages/CyPageCloudCostManagementDashboard.vue'),
        props: true,
        meta: {
          ...getMetaDefaults('cloudCostManagementSection', {
            permissionsKey: 'cloudCostManagement.index',
          }),
          layout: { name: 'app' },
        },
        beforeEnter: handleCCMRedirect,
      },
      {
        path: 'settings',
        name: 'cloudCostManagementSettings',
        component: () => import('@/pages/CyPageCloudCostManagementSettings.vue'),
        props: true,
        meta: {
          ...getMetaDefaults('cloudCostManagementSection', {
            permissionsKey: 'cloudCostManagement.accounts.list',
          }),
          layout: { name: 'app' },
        },
        beforeEnter: handleCCMRedirect,
      },
      {
        path: ':providerCanonical/provider-detail',
        name: 'cloudCostManagementProviderDetail',
        component: () => import('@/pages/CyPageCloudCostManagementProviderDetail.vue'),
        props: true,
        meta: {
          ...getMetaDefaults('cloudCostManagementSection', {
            permissionsKey: 'cloudCostManagement.index',
          }),
          showBackBtn,
          backRouteTo: 'cloudCostManagementDashboard',
          layout: { name: 'app' },
        },
        beforeEnter: handleCCMRedirect,
      },
      {
        path: 'welcome',
        name: 'cloudCostManagementWelcome',
        component: () => import('@/pages/CyPageCloudCostManagementWelcome.vue'),
        props: true,
        meta: {
          ...getMetaDefaults('cloudCostManagementSection', {
            permissionsKey: 'cloudCostManagement.index',
          }),
          layout: {
            name: 'app',
            fitOnScreen,
          },
        },
        beforeEnter: async ({ params }, from) => {
          // if from.name is not null it means we're coming from an existing page,
          // which means we just did a redirect, which means there's need to check
          // on accounts or redirect again
          if (_.$isEmpty(from.name) && (await hasCCMAccounts())) {
            return { name: 'cloudCostManagementDashboard', params }
          }
        },
      },
    ],
  },
]
/** @type {VueRouteRecord[]} */
const roles = [
  {
    path: 'roles',
    name: 'roles',
    component: () => import('@/pages/CyPageRoles.vue'),
    props: true,
    meta: {
      ...getMetaDefaults('roles', {
        permissionsKey: 'roles.index',
        isListPage,
      }),
      layout: { name: 'app' },
      clickAction: ({ $gtm }) => {
        $gtm.trackEvent(gtmSecurityEvents.securityRoles)
      },
    },
  },
  {
    path: 'roles/create',
    name: 'newRole',
    component: () => import('@/pages/CyPageRole.vue'),
    props: true,
    meta: {
      ...getMetaDefaults('roles', {
        permissionsKey: 'roles.create',
        showBackBtn,
      }),
      layout: {
        name: 'app',
        hideFooter,
      },
    },
  },
  {
    path: 'roles/:roleCanonical',
    name: 'role',
    component: () => import('@/pages/CyPageRole.vue'),
    props: true,
    meta: {
      ...getMetaDefaults('roles', {
        permissionsKey: 'roles.details',
        showBackBtn,
      }),
      layout: {
        name: 'app',
        hideFooter,
      },
    },
    beforeEnter: (to, _from) => {
      return redirectTo404IfHasInvalidParam(to)
    },
  },
]
/** @type {VueRouteRecord[]} */
const infraPolicies = [
  {
    path: 'infra-policies',
    name: 'infraPolicies',
    component: () => import('@/pages/CyPageInfraPolicies.vue'),
    props: true,
    meta: {
      ...getMetaDefaults('infraPolicies', {
        permissionsKey: 'infraPolicies.index',
        isListPage,
      }),
      layout: { name: 'app' },
      clickAction: ({ $gtm }) => {
        $gtm.trackEvent(gtmSecurityEvents.securityInfraPolicies)
      },
    },
  },
  {
    path: 'infra-policies/create',
    name: 'newInfraPolicy',
    component: () => import('@/pages/CyPageInfraPolicy.vue'),
    props: true,
    meta: {
      ...getMetaDefaults('infraPolicies', {
        permissionsKey: 'infraPolicies.create',
        showBackBtn,
      }),
      layout: {
        name: 'app',
        fitOnScreen,
        hideFooter,
      },
    },
  },
  {
    path: 'infra-policies/:infraPolicyCanonical',
    name: 'infraPolicy',
    component: () => import('@/pages/CyPageInfraPolicy.vue'),
    props: true,
    meta: {
      ...getMetaDefaults('infraPolicies', {
        permissionsKey: 'infraPolicies.details',
        showBackBtn,
      }),
      layout: {
        name: 'app',
        fitOnScreen,
        hideFooter,
      },
    },
    beforeEnter: (to, _from) => {
      return redirectTo404IfHasInvalidParam(to)
    },
  },
]
/** @type {VueRouteRecord[]} */
const inventory = [
  {
    path: 'inventory',
    name: 'inventory',
    component: () => import('@/pages/CyPageInventory.vue'),
    props: (route) => ({
      requestedResourceId: Number(route.query.resourceId),
      requestedResourceTab: route.query.resourceTab,
    }),
    meta: {
      ...getMetaDefaults('inventory', {
        isListPage,
        permissionsKey: 'inventory.index',
      }),
      layout: {
        name: 'app',
        hideFooter,
      },
    },
  },
]
/** @type {VueRouteRecord[]} */
const resources = [
  {
    path: 'resources',
    name: 'resourcesSection',
    component: () => import('@/pages/CyPageTabs.vue'),
    props: (route) => {
      return {
        ...route.params,
        routeName: 'resourcesSection',
        hideTabs: false,
      }
    },
    meta: {
      ...getMetaDefaults('resources'),
      layout: { name: 'app' },
      tabs: () => [
        'inventory',
      ],
    },
    children: [
      ...inventory,
    ],
  },
]
/** @type {VueRouteRecord[]} */
const apiKeys = [
  {
    path: 'api-keys',
    name: 'apiKeys',
    component: () => import('@/pages/CyPageApiKeys.vue'),
    props: true,
    meta: {
      ...getMetaDefaults('apiKeys', {
        permissionsKey: 'apiKeys.index',
        isListPage,
      }),
      layout: { name: 'app' },
      clickAction: ({ $gtm }) => {
        $gtm.trackEvent(gtmSecurityEvents.securityApiKeys)
      },
    },
  },
  {
    path: 'api-keys/create',
    name: 'newApiKey',
    component: () => import('@/pages/CyPageApiKey.vue'),
    props: true,
    meta: {
      ...getMetaDefaults('apiKeys', {
        permissionsKey: 'apiKeys.create',
        showBackBtn,
      }),
      layout: {
        name: 'app',
        hideFooter,
      },
    },
  },
  {
    path: 'api-keys/:apiKeyCanonical',
    name: 'apiKey',
    component: () => import('@/pages/CyPageApiKey.vue'),
    props: true,
    meta: {
      ...getMetaDefaults('apiKeys', {
        permissionsKey: 'apiKeys.details',
        showBackBtn,
      }),
      layout: {
        name: 'app',
        hideFooter,
      },
    },
    beforeEnter: (to, _from) => {
      return redirectTo404IfHasInvalidParam(to)
    },
  },
]
/** @type {VueRouteRecord[]} */
const security = [
  {
    path: 'security',
    name: 'securitySection',
    component: () => import('@/pages/CyPageTabs.vue'),
    props: (route) => {
      const tabsToHide = [
        'credential',
        'newCredential',
        'infraPolicy',
        'newInfraPolicy',
        'role',
        'newRole',
        'apiKey',
        'newApiKey',
      ]

      return {
        ...route.params,
        routeName: 'securitySection',
        hideTabs: tabsToHide.includes(route.name),
      }
    },
    meta: {
      ...getMetaDefaults('roles'),
      layout: { name: 'app' },
      tabs: () => ['credentials', 'infraPolicies', 'roles', 'apiKeys'],
      clickAction: ({ $gtm }) => {
        $gtm.trackEvent(gtmSecurityEvents.security)
      },
    },
    children: [
      ...credentials,
      ...infraPolicies,
      ...roles,
      ...apiKeys,
    ],
  },
]
/** @type {VueRouteRecord[]} */
const stacks = [
  {
    path: 'stacks',
    name: 'stacksSection',
    component: () => import('@/pages/CyPageTabs.vue'),
    props: (route) => {
      const tabsToHide = [
        'newCatalogRepository',
        'editCatalogRepository',
        'catalogRepository',
      ]
      return {
        ...route.params,
        routeName: 'stacksSection',
        hideTabs: tabsToHide.includes(route.name),
      }
    },
    meta: {
      ...getMetaDefaults('stacks'),
      layout: { name: 'app' },
      tabs: () => ['stacks', 'catalogRepositories', 'infraTools'],
    },
    children: [
      {
        path: 'all-stacks',
        name: 'stacks',
        component: () => import('@/pages/CyPageStacks.vue'),
        props: true,
        meta: {
          ...getMetaDefaults('stacks', {
            permissionsKey: 'stacks.index',
          }),
          layout: {
            name: 'app',
            hideFooter,
          },
          clickAction: ({ $gtm }) => {
            $gtm.trackEvent(gtmStacksEvents.stacks)
          },
        },
      },
      ...catalogRepositories,
      {
        path: 'stacks/tools',
        name: 'infraTools',
        component: () => import('@/pages/CyPageInfraTools.vue'),
        props: true,
        meta: {
          ...getMetaDefaults('stacks', {
            permissionsKey: 'stacks.tools',
          }),
          layout: { name: 'app' },
        },
      },
    ],
  },
  {
    path: 'stacks/from-template',
    name: 'stackFromTemplate',
    component: () => import('@/pages/CyPageStackFromTemplate.vue'),
    props: true,
    meta: {
      ...getMetaDefaults('stacks', {
        permissionsKey: 'stacks.create',
      }),
      layout: { name: 'app' },
    },
  },
  {
    path: 'stacks/editor',
    name: 'formsEditor',
    component: () => import('@/pages/CyPageFormsEditor.vue'),
    props: true,
    meta: {
      ...getMetaDefaults('formsEditor', {
        permissionsKey: 'stacks.create',
        backRouteTo: 'infraTools',
        showBackBtn,
      }),
      layout: {
        name: 'app',
        fitOnScreen,
        hideFooter,
        noPadding,
      },
    },
  },
  {
    path: 'stacks/:stackRef',
    name: 'stack',
    component: () => import('@/pages/CyPageTabs.vue'),
    props: (route) => ({
      ...route.params,
      routeName: 'stack',
    }),
    meta: {
      ...getMetaDefaults('stacks', {
        permissionsKey: 'stacks.details',
        backRouteTo: 'stacks',
        figmaKey: 'stack',
        showBackBtn,
      }),
      layout: {
        name: 'app',
        hideFooter,
        header: defineAsyncComponent(() => import('@/components/CyHeaderVariantStack.vue')),
      },
      tabs: () => ['stackOverview', 'stackVersions', 'stackAdoption'],
    },
    beforeEnter: (to, _from) => {
      return redirectTo404IfHasInvalidParam(to)
    },
    children: [
      {
        path: 'overview',
        name: 'stackOverview',
        component: () => import('@/pages/CyPageStackOverview.vue'),
        props: true,
        meta: {
          ...getMetaDefaults('stacks', {
            permissionsKey: 'stacks.details',
            backRouteTo: 'stacks',
            figmaKey: 'stack',
            showBackBtn,
          }),
          layout: {
            name: 'app',
            hideFooter,
            header: defineAsyncComponent(() => import('@/components/CyHeaderVariantStack.vue')),
          },
        },
      },
      {
        path: 'versions',
        name: 'stackVersions',
        component: () => import('@/pages/CyPageStackVersions.vue'),
        props: true,
        meta: {
          ...getMetaDefaults('stacks', {
            permissionsKey: 'stacks.details',
            backRouteTo: 'stacks',
            showBackBtn,
          }),
          layout: {
            name: 'app',
            hideFooter,
            header: defineAsyncComponent(() => import('@/components/CyHeaderVariantStack.vue')),
          },
        },
      },
      {
        path: 'adoption',
        name: 'stackAdoption',
        component: () => import('@/pages/CyPageStackAdoption.vue'),
        props: true,
        meta: {
          ...getMetaDefaults('stacks', {
            permissionsKey: 'stacks.details',
            backRouteTo: 'stacks',
            showBackBtn,
          }),
          layout: {
            name: 'app',
            hideFooter,
            header: defineAsyncComponent(() => import('@/components/CyHeaderVariantStack.vue')),
          },
        },
      },
    ],
  },
]
/** @type {VueRouteRecord[]} */
const teams = [
  {
    path: 'teams',
    name: 'teams',
    component: () => import('@/pages/CyPageTeams.vue'),
    redirect: { name: 'orgSettings', query: { section: 'teams' } },
    props: true,
    meta: {
      ...getMetaDefaults('teams', {
        permissionsKey: 'teams.index',
        isListPage,
      }),
      layout: {
        name: 'app',
        header: defineAsyncComponent(() => import('@/components/CyHeaderVariantOrganization.vue')),
      },
      clickAction: ({ $gtm }) => {
        $gtm.trackEvent(gtmOrganizationsEvents.organizationsTeams)
      },
    },
  },
  {
    path: 'teams/create',
    name: 'newTeam',
    component: () => import('@/pages/CyPageTeam.vue'),
    props: true,
    meta: {
      ...getMetaDefaults('teams', {
        permissionsKey: 'teams.create',
        showBackBtn,
      }),
      layout: {
        name: 'app',
        hideFooter,
      },
    },
  },
  {
    path: 'teams/:teamCanonical',
    name: 'team',
    component: () => import('@/pages/CyPageTeam.vue'),
    props: true,
    meta: {
      ...getMetaDefaults('teams', {
        permissionsKey: 'teams.details',
        showBackBtn,
      }),
      layout: {
        name: 'app',
        hideFooter,
      },
    },
    beforeEnter: (to, _from) => {
      return redirectTo404IfHasInvalidParam(to)
    },
  },
]
/** @type {VueRouteRecord[]} */
const workers = [
  {
    path: 'workers',
    name: 'workers',
    component: () => import('@/pages/CyPageWorkers.vue'),
    redirect: { name: 'orgSettings', query: { section: 'workers' } },
    props: true,
    meta: {
      ...getMetaDefaults('organizations', {
        permissionsKey: 'organization.workers',
        isListPage,
      }),
      layout: {
        name: 'app',
        header: defineAsyncComponent(() => import('@/components/CyHeaderVariantOrganization.vue')),
      },
      clickAction: ({ $gtm }) => {
        $gtm.trackEvent(gtmOrganizationsEvents.organizationsWorkers)
      },
    },
  },
]
/** @type {VueRouteRecord[]} */
const orgConfig = [
  {
    path: 'settings',
    name: 'orgSettings',
    component: () => import('@/pages/CyPageOrgSettings.vue'),
    props: true,
    meta: {
      ...getMetaDefaults('organizations', {
        permissionsKey: 'organization.settings.index',
        remainOnPageAfterOrgSwitch,
      }),
      layout: {
        name: 'app',
        hideFooter,
        header: defineAsyncComponent(() => import('@/components/CyHeaderVariantOrganization.vue')),
      },
      clickAction: ({ $gtm }) => {
        $gtm.trackEvent(gtmOrganizationsEvents.organizationsGeneral)
      },
    },
  },
  {
    path: 'settings/plugins/:pluginRegistryId/:pluginSlug',
    name: 'orgSettingsPlugin',
    component: () => import('@/pages/CyPageTabs.vue'),
    props: (route) => ({
      routeName: 'orgSettingsPlugin',
      hideTabs: route.query.hideTabs === 'true',
    }),
    redirect: { name: 'orgSettingsPluginOverview' },
    meta: {
      ...getMetaDefaults('orgSettings', {
        permissionsKey: 'organization.settings.plugins.details',
        backRouteTo: 'orgSettings?section=plugins',
        figmaKey: 'organizations',
        showBackBtn,
      }),
      layout: {
        name: 'app',
        hideFooter,
        header: defineAsyncComponent(() => import('@/components/CyHeaderVariantPlugin.vue')),
      },
      tabs: () => ['orgSettingsPluginOverview', 'orgSettingsPluginVersions', 'orgSettingsPluginWidgets'],
    },
    children: [
      {
        path: 'overview',
        name: 'orgSettingsPluginOverview',
        component: () => import('@/pages/CyPagePluginOverview.vue'),
        meta: {
          ...getMetaDefaults('orgSettings', {
            permissionsKey: 'organization.settings.plugins.details',
            figmaKey: 'organizations',
          }),
          breadcrumb: {},
        },
      },
      {
        path: 'versions',
        name: 'orgSettingsPluginVersions',
        component: () => import('@/pages/CyPagePluginVersions.vue'),
        meta: {
          ...getMetaDefaults('orgSettings', {
            permissionsKey: 'organization.settings.plugins.versions',
            figmaKey: 'organizations',
          }),
          breadcrumb: {},
        },
      },
      {
        path: 'widgets',
        name: 'orgSettingsPluginWidgets',
        component: () => import('@/pages/CyPagePluginWidgets.vue'),
        meta: {
          ...getMetaDefaults('orgSettings', {
            permissionsKey: 'organization.settings.plugins.widgets',
            figmaKey: 'organizations',
          }),
          breadcrumb: {},
        },
      },
    ],
  },
  {
    path: 'settings/appearance/create',
    name: 'newOrgSettingsAppearance',
    component: () => import('@/pages/CyPageOrgAppearance.vue'),
    props: true,
    meta: {
      ...getMetaDefaults('organizations', {
        permissionsKey: 'organization.settings.appearance.create',
        backRouteTo: 'orgSettings?section=appearance',
        showBackBtn,
      }),
      layout: {
        name: 'app',
        hideFooter,
        fitOnScreen,
      },
    },
  },
  {
    path: 'settings/appearance/:appearanceCanonical',
    name: 'orgSettingsAppearance',
    component: () => import('@/pages/CyPageOrgAppearance.vue'),
    props: true,
    meta: {
      ...getMetaDefaults('organizations', {
        permissionsKey: 'organization.settings.appearance.details',
        backRouteTo: 'orgSettings?section=appearance',
        showBackBtn,
      }),
      layout: {
        name: 'app',
        hideFooter,
        fitOnScreen,
      },
      clickAction: () => {
        // TODO: add GTM tracker for appearance page click
        // $gtm.trackEvent(gtmOrganizationsEvents.organizationsGeneral)
      },
    },
    beforeEnter: (to, _from) => {
      if (to.params.appearanceCanonical === 'default') {
        return {
          name: 'forbidden',
          params: {
            permissionsKey: 'organization.settings.appearance.details',
          },
          state: {
            0: to.fullPath,
            backRouteTo: 'orgSettings',
          },
          replace: true,
        }
      }
      return redirectTo404IfHasInvalidParam(to)
    },
  },
  {
    path: 'settings/terraform',
    name: 'orgSettingsTerraform',
    component: () => import('@/pages/CyPageOrgTerraform.vue'),
    props: true,
    meta: {
      ...getMetaDefaults('organizations', {
        permissionsKey: 'organization.settings.terraform',
        backRouteTo: 'orgSettings?section=terraform',
        showBackBtn,
      }),
      layout: {
        name: 'app',
        hideFooter,
      },
    },
  },
  ...workers,
  ...members,
  ...teams,
]
/** @type {VueRouteRecord[]} */
const devRoutes = [
  {
    path: '/dev',
    name: 'dev',
    component: () => import('@/pages/CyPageTabs.vue'),
    props: (route) => ({
      ...route.params,
      routeName: 'dev',
    }),
    meta: {
      ...getMetaDefaults('dev'),
      layout: { name: 'app' },
      tabs: () => [
        'sandbox',
      ],
    },
    children: [
      {
        path: 'sandbox',
        name: 'sandbox',
        component: () => import('@/sandbox/sandbox.vue'),
        props: true,
        meta: {
          ...getMetaDefaults('dev'),
          layout: { name: 'app' },
          actions: {
            readMembers: 'GetOrgMembers',
          },
        },
      },
    ],
  },
]
/** @type {VueRouteRecord[]} */
const publicRoutes = [
  {
    path: '/',
    name: 'home',
    redirect: { name: 'login' },
    meta: {
      ...getMetaDefaults('home'),
      isPublic,
    },
  },
  {
    path: '/login/:orgCanonicalToLogIn?',
    name: 'login',
    props: true,
    component: () => import('@/pages/CyPageLogIn.vue'),
    meta: {
      ...getMetaDefaults('login'),
      layout: { name: 'public', isTwoPane },
      isPublic,
    },
    async beforeEnter ({ params: { orgCanonicalToLogIn } }, _from) {
      const licenceStore = useLicenceStore()
      const rootStore = useRootStore()
      licenceStore.SET_FETCHED(false)
      if (orgCanonicalToLogIn) await rootStore.FETCH_APP_APPEARANCE(orgCanonicalToLogIn)
    },
  },
  {
    // Support impersonation (ENG-408): log in as yourself, then enter the user to impersonate.
    // Static segment, so it wins over /login/:orgCanonicalToLogIn?. Who may impersonate is the
    // root-organization allowlist, checked by the backend on POST /user/impersonate.
    path: '/login/impersonate',
    name: 'impersonate',
    props: { impersonate: true },
    component: () => import('@/pages/CyPageLogIn.vue'),
    meta: {
      ...getMetaDefaults('login'),
      layout: { name: 'public', isTwoPane },
      isPublic,
    },
    beforeEnter (_to, _from) {
      const licenceStore = useLicenceStore()
      licenceStore.SET_FETCHED(false)
    },
  },
  {
    path: '/signup/:orgCanonicalToLogIn?',
    name: 'signUp',
    component: () => import('@/pages/CyPageSignUp.vue'),
    meta: {
      ...getMetaDefaults('login'),
      layout: { name: 'public', isTwoPane },
      isPublic,
    },
    async beforeEnter ({ params: { orgCanonicalToLogIn }, query }, _from) {
      const rootStore = useRootStore()
      if (orgCanonicalToLogIn) await rootStore.FETCH_APP_APPEARANCE(orgCanonicalToLogIn)
      await rootStore.GET_APP_CONFIG(orgCanonicalToLogIn)
      const isOAuthSignupRedirect = ['1', 'true'].includes(query.isOauthSignupRedirect)
      if (!rootStore.appConfig?.authentication?.email && !isOAuthSignupRedirect) {
        return { name: 'login' }
      }
    },
  },
  {
    path: '/mfa/login-link-notice/:email',
    name: 'MFALoginLinkNotice',
    component: () => import('@/pages/CyPageMfaLoginLinkNotice.vue'),
    meta: {
      ...getMetaDefaults('login'),
      layout: { name: 'public', isTwoPane },
      isPublic,
    },
    props: true,
  },
  {
    path: '/mfa/invalid-login-link',
    name: 'MFAInvalidLoginLink',
    component: () => import('@/pages/CyPageMfaInvalidLoginLink.vue'),
    meta: {
      ...getMetaDefaults('login'),
      layout: { name: 'public', isTwoPane },
      isPublic,
    },
  },
  {
    path: '/marketplace/new-org',
    name: 'marketplaceNewOrganization',
    component: () => import('@/pages/CyPageMarketplaceNewOrganization.vue'),
    meta: {
      ...getMetaDefaults('organizations'),
      layout: { name: 'public', isTwoPane },
      isPublic,
    },
  },
  {
    path: '/account-verification',
    name: 'accountVerification',
    component: () => import('@/pages/CyPageAccountVerification.vue'),
    props: true,
    meta: {
      ...getMetaDefaults('verifyAccount'),
      layout: { name: 'public', isTwoPane },
      isPublic,
    },
  },
  {
    path: '/logout/:orgCanonicalToLogIn?',
    name: 'logout',
    meta: {
      ...getMetaDefaults('logout'),
      isPublic,
      handlerPage,
    },
    async beforeEnter ({ params }, _from) {
      const authStore = useAuthStore()
      // The page being left stays mounted until this navigation ends, and it reads the stores the
      // logout resets: hide it first, else it fails to render and leaves the app blank. The flag
      // is cleared once the next page is shown (endLoggingOut)
      useLayoutStore().SET_LOGGING_OUT(true)
      await nextTick()
      // On a cold boot of /logout nothing has restored the session yet (checkAuthBeforeEach skips
      // this route), so the impersonated token is only known after reading it back from storage
      authStore.RESTORE_CREDENTIALS()
      // Logging out of an impersonated session returns to the support user's own session (ENG-408)
      if (authStore.isImpersonating) return authStore.STOP_IMPERSONATION()
      // Awaited: un-awaited, the login page's session restore raced the clearing of the
      // credentials and could land a logged-out user on a 403 dashboard
      await authStore.LOGOUT()
      return { name: 'login', params }
    },
  },
  {
    path: '/forbidden',
    name: 'forbidden',
    component: () => import('@/pages/CyPageForbidden.vue'),
    props: true,
    meta: {
      ...getMetaDefaults('forbidden'),
      isPublic,
    },
  },
  {
    path: '/invitation/:token',
    name: 'invitation',
    meta: {
      ...getMetaDefaults('invite'),
      isPublic,
      handlerPage,
    },
    async beforeEnter ({ params }, _from) {
      const authStore = useAuthStore()
      const token = _.get(params, 'token', null)
      authStore.LOGOUT()
      await authStore.RETRIEVE_INVITATION_DATA(token)

      const { email } = authStore.invitation
      return { name: 'signUp', query: { email } }
    },
  },
  {
    path: '/user/email/authentication/:token',
    name: 'authenticateEmail',
    meta: {
      ...getMetaDefaults('authenticateEmail', {
        permissionsKey: 'user',
      }),
      isPublic,
      handlerPage,
    },

    async beforeEnter ({ params: { token }, query }, _from) {
      const rootStore = useRootStore()
      const licenceStore = useLicenceStore()
      const orgStore = useOrganizationStore()
      const authStore = useAuthStore()

      const orgCanonicalFromQuery = _.get(query, 'organization_canonical', null)
      const orgCanonical = orgCanonicalFromQuery || rootStore.orgCanonical

      licenceStore.SET_FETCHED(false)
      if (orgCanonical) rootStore.FETCH_APP_APPEARANCE(orgCanonical)

      await authStore.LOGIN({ token })

      if (rootStore.hasOrgs) {
        const nextCanonical = getValidOrganization(rootStore.organizations, orgCanonical).canonical

        if (!_.$isEmpty(nextCanonical)) {
          await orgStore.CHANGE_ORGANIZATION({ nextCanonical })
          return rootStore.defaultRoute
        }
      }
      await orgStore.SET_ORGANIZATION()
      return { name: 'organizations' }
    },
  },
  {
    path: '/user/email/verified/:token',
    name: 'verifyEmail',
    meta: {
      ...getMetaDefaults('verifyEmail', {
        permissionsKey: 'user',
      }),
      isPublic,
      handlerPage,
    },
    async beforeEnter ({ params, query }, _from) {
      const token = _.get(params, 'token', null)
      const authStore = useAuthStore()
      authStore.LOGOUT()
      await authStore.VERIFY_EMAIL(token)
      return { name: 'login', params: { ...params }, query: { ...query, action: 'emailConfirmed' } }
    },
  },
  {
    path: '/user/password/reset/:token',
    name: 'resetPassword',
    component: () => import('@/pages/CyPageResetPassword.vue'),
    props: true,
    meta: {
      ...getMetaDefaults('login', {
        permissionsKey: 'user',
      }),
      layout: { name: 'public', isTwoPane },
      isPublic,
    },
  },
  {
    path: '/status',
    name: 'status',
    component: () => import('@/pages/CyPageStatus.vue'),
    meta: {
      ...getMetaDefaults('status'),
      isPublic,
    },
    beforeEnter (to, _from) {
      const authStore = useAuthStore()
      to.meta.layout = {
        ...getLayout(authStore.isAuthenticated),
        isWhite: !authStore.isAuthenticated,
      }
    },
  },
  {
    path: '/oidc-popup-callback', // Common callback URL for all OAuth providers
    name: 'oidcPopupCallback',
    alias: ['/saml2-callback', '/oauth2-callback'], // Aliases for backward compatibility
    component: () => import('@/components/CySsoPopupCallback.vue'),
    meta: {
      ...getMetaDefaults('oidc'),
      isPublic,
    },
  },
  {
    path: '/privacy-policy',
    name: 'privacy',
    component: () => import('@/pages/CyPagePrivacyPolicy.vue'),
    meta: {
      ...getMetaDefaults('privacy'),
      layout: { name: 'public', isWhite: true },
      isPublic,
    },
  },
  {
    path: '/terms',
    name: 'terms',
    component: () => import('@/pages/CyPageTerms.vue'),
    meta: {
      ...getMetaDefaults('terms'),
      layout: { name: 'public', isWhite: true },
      isPublic,
    },
  },
  {
    path: '/no-organization-access',
    name: 'noOrgAccess',
    component: () => import('@/pages/CyPageNoOrgAccess.vue'),
    meta: {
      ...getMetaDefaults('noOrgAccess'),
      layout: { name: 'public', isWhite: true },
      isPublic,
    },
  },
  {
    path: '/access-denied',
    name: 'oidcEjected',
    component: () => import('@/pages/CyPageOidcEjected.vue'),
    meta: {
      ...getMetaDefaults('oidcEjected'),
      layout: { name: 'public', isWhite: true },
      isPublic,
    },
  },
]
/** @type {VueRouteRecord[]} */
const orgDependentRoutes = [
  {
    path: '/organizations/:orgCanonical',
    name: 'organization',
    component: () => import('@/pages/CyPageTabs.vue'),
    props: (route) => ({
      ...route.params,
      routeName: 'organization',
      hideTabs: [
        'orgSettingsAppearance',
        'newOrgSettingsAppearance',
        'orgSettingsTerraform',
        'orgSettings',
        'member',
        'team',
        'newTeam',
        'memberInvite',
      ].includes(route.name) || !_.map(orgConfig, 'name').includes(route.name),
    }),
    meta: {
      ...getMetaDefaults('organizations'),
      layout: { name: 'app' },
      tabs: () => [
        'orgSettings',
        'workers',
        'members',
        'teams',
      ],
    },
    children: [
      dashboard,
      ...orgConfig,
      ...environment,
      ...cloudAccount,
      ...cloudCostManagement,
      ...observability,
      ...resources,
      ...security,
      ...stacks,
      ...projects,
      {
        path: 'widgets/:widgetSlug/:pathSuffix(.*)*',
        name: 'pluginWidget',
        component: () => import('@/pages/CyPageWidget.vue'),
        props: true,
        meta: {
          ...getMetaDefaults('dashboard'),
          layout: { name: 'app' },
          requiredActions: ['GetPluginWidgetData'],
        },
        beforeEnter: ensurePluginWidgetExists('org'),
      },
    ],
  },
]

/**
 * Array of user page definitions.
 *
 * Each entry is a tuple representing a route, structured as:
 * @type {[string, string, string][]}
 * [0] Route path segment (appended to parent route)
 * [1] Route name (used in Vue Router)
 * [2] Component suffix (used for dynamic import)
 */
const userPages = [
  ['account', 'userAccount', 'UserAccount'],
  ['emails', 'userEmails', 'UserEmails'],
  ['security', 'userSecurity', 'UserSecurity'],
  ['notifications-settings', 'userNotificationsSettings', 'UserNotificationsSettings'],
  ['watch-rules', 'userWatchRules', 'UserWatchRules'],
]

/**
 * Shared meta configuration for all user routes.
 *
 * Combines:
 * - Default meta from getMetaDefaults
 * - Layout configuration
 *
 * @type {object}
 */
const sharedUserMeta = {
  ...getMetaDefaults('profile', { permissionsKey: 'user' }),
  layout: { name: 'app' },
}

/** @type {VueRouteRecord[]} */
const userRoutes = [
  {
    path: '/notifications',
    name: 'notifications',
    component: () => import('@/pages/CyPageNotifications.vue'),
    meta: {
      ...getMetaDefaults('profile', {
        permissionsKey: 'user',
      }),
      layout: { name: 'app' },
    },
  },
  {
    path: '/user',
    name: 'profile',
    component: () => import('@/pages/CyPageProfile.vue'),
    redirect: { name: 'userAccount' },
    meta: {
      ...getMetaDefaults('profile', {
        permissionsKey: 'user',
      }),
      layout: { name: 'app' },
    },
    children: userPages.map(([path, name, page]) => ({
      path,
      name,
      component: () => import(`@/pages/CyPage${page}.vue`),
      meta: sharedUserMeta,
    })),
  },
  {
    path: '/watch-rules/create',
    name: 'newUserWatchRule',
    component: () => import('@/pages/CyPageUserWatchRule.vue'),
    meta: {
      ...getMetaDefaults('profile', {
        permissionsKey: 'user',
        backRouteTo: 'userWatchRules',
        showBackBtn,
      }),
      layout: { name: 'app' },
    },
  },
  {
    path: '/watch-rules/:watchRuleCanonical/edit',
    name: 'userWatchRuleEdit',
    component: () => import('@/pages/CyPageUserWatchRule.vue'),
    props: true,
    meta: {
      ...getMetaDefaults('profile', {
        permissionsKey: 'user',
        backRouteTo: 'userWatchRules',
        showBackBtn,
      }),
      layout: { name: 'app' },
    },
  },
  {
    path: '/watch-rules/:watchRuleCanonical',
    name: 'userWatchRule',
    component: () => import('@/pages/CyPageUserWatchRuleDetail.vue'),
    props: true,
    meta: {
      ...getMetaDefaults('profile', {
        permissionsKey: 'user',
        backRouteTo: 'userWatchRules',
        showBackBtn,
      }),
      layout: {
        name: 'app',
        noPadding,
      },
    },
  },
]

export function getMetaDefaults (appSection, {
  backRouteTo = appSection,
  figmaKey = appSection,
  permissionsKey = 'organizations',
  ...options
} = {}) {
  return {
    actions: _.$get(actionsWithResources, `${permissionsKey}.actions`, {}),
    appSection,
    backRouteTo,
    breadcrumb: {},
    figmaKey,
    hasConditionalBreadcrumb: options.hasConditionalBreadcrumb ?? false,
    isAlpha: _.some([alphaRoutes.includes(appSection), options.isAlpha]) ?? false,
    isBeta: _.some([betaAppSections.includes(appSection), options.isBeta]) ?? false,
    isListPage: options.isListPage ?? false,
    permissionsKey,
    remainOnPageAfterOrgSwitch: options.remainOnPageAfterOrgSwitch ?? false,
    requiredActions: _.$get(actionsWithResources, `${permissionsKey}.requiredActions`, []),
    showBackBtn: options.showBackBtn ?? false,
  }
}

export function setIdProp (route) {
  const props = { ...route.params }
  const idKey = _.keys(props).find((key) => key.endsWith('Id'))
  props[idKey] = _.$isEmpty(props[idKey]) ? null : Number(props[idKey])
  return props
}

export function redirectTo404IfHasInvalidParam (to) {
  const hasAllValidCanonicalsAndIds = _.every(_.toPairs(to.params), isParamValid)
  if (hasAllValidCanonicalsAndIds) return

  const { backRouteTo } = _.cloneDeep(to.meta)
  return { name: 'notFound', state: { 0: to.fullPath, backRouteTo }, replace: true }
}

export function getLayout (isAuth) {
  const appLayout = {
    name: 'app',
    variant: 'one-column',
    fitOnScreen,
  }
  const publicLayout = { name: 'public' }
  return isAuth ? appLayout : publicLayout
}

async function hasCCMAccounts () {
  const devStore = useDevStore()
  if (!devStore.$showFeature.cloudEnvironments) return true
  const ccmStore = useCloudCostManagementStore()
  await ccmStore.GET_ACCOUNTS()
  return !_.$isEmpty(ccmStore.accounts)
}

async function handleCCMRedirect ({ name, params }) {
  const shouldRedirect = name !== 'cloudCostManagementWelcome' && !(await hasCCMAccounts())
  if (shouldRedirect) return { name: 'cloudCostManagementWelcome', params }
}

function isParamValid ([paramKey, paramValue]) {
  const paramCategory = paramKey.match(REGEX.ROUTE.PARAM_CATEGORY)?.at(2)
  const paramType = { Id: 'number', Ref: 'ref' }[paramCategory] ?? 'string'

  if (_.isNil(paramValue)) return false
  if (!paramCategory) return true

  const paramValidators = {
    /** Default checks, per paramType. */
    byType: {
      ref: /^[\w-]{2,100}:[\w-]{2,100}$/,
      string: /^[\w-]{2,100}$/,
      number: /^(?!0)[(?!0)1-9]+$/,
    },
    /** Custom checks, per paramKey. _Add edge cases here._ */
    custom: {
      projectCanonical: /^[\w-]{1,100}$/,
      envCanonical: /^[\w.-]{2,100}$/,
      componentCanonical: /^[\w.-]{2,100}$/,
      pipelineCanonical: /^[\w.-]{2,100}$/,
      jobCanonical: /^[\w- ]{3,100}$/,
    },
  }

  return (paramValidators.custom[paramKey] ?? paramValidators.byType[paramType]).test(paramValue)
}

/** @type {VueRouteRecord[]} */
export default [
  ...publicRoutes,
  ...redirects,
  organizations,
  ...orgDependentRoutes,
  ...userRoutes,
  ...(isStagingDevOrTesting ? devRoutes : []),
  // 404 (when all other routes fail) //
  {
    path: '/:pathMatch(.*)*',
    name: 'notFound',
    component: () => import('@/pages/CyPageNotFound.vue'),
    props: true,
    meta: {
      ...getMetaDefaults('404'),
      isPublic,
    },
    beforeEnter (_to, from) {
      const authStore = useAuthStore()
      const fromRoute = _.pick(from, ['path', 'name', 'query', 'params'])
      if (_.isEqual(fromRoute, authStore.defaultRoute)) return

      // This case happens when the user has accessed for any reason
      // to a route whose path doesn't match to any of the ones defined
      // in the application, so it's resolved by this route which is the
      // one which matches everything (i.e. path: '*')
      authStore.SET_DEFAULT_ROUTE({ name: import.meta.env.VUE_APP_DEFAULT_ROUTE_NAME_REDIRECT })
    },
  },
]
